import { ReactNode } from 'react';

import { Regulations } from '../../api/fishrules/getSpeciesRegulationsAtLocation';
import { hasRestrictions } from '../../components/ProhibitedMethods/helpers';
import { ProhibitedMethods } from '../../components/ProhibitedMethods/ProhibitedMethods';
import { Text } from '../../components/Text/Text';
import { convertHtmlToReactElement } from '../../components/TextFromHTML/helpers';
import { TextFromHTML } from '../../components/TextFromHTML/TextFromHTML';
import { IBoundary } from '@domain/regulations/hooks/useRegulationBoundaryAtLocation';

interface AdditionalInformation {
  order: number;
  visible: boolean;
  title: string;
  body: ReactNode;
}

const getAdditionalInformation = ({
  regulation,
  boundaries,
}: {
  regulation: Regulations;
  boundaries: IBoundary[];
}): AdditionalInformation[] => {
  const locationSpecificInformation = boundaries
    .map(({ regulationNotes }) => regulationNotes)
    .filter(regulation => regulation);

  const ADDITIONAL_INFORMATION: Record<string, AdditionalInformation> = {
    prohibitedMethods: {
      order: 1,
      visible: hasRestrictions(regulation),
      title: 'Restrictions & requirements',
      body: <ProhibitedMethods regulation={regulation} />,
    },
    requiredLicenses: {
      order: 2,
      visible: Boolean(regulation.additional_licenses_required),
      title: 'Required licenses',
      body: <TextFromHTML html={regulation.additional_licenses_required} />,
    },
    locationRegulationNotes: {
      order: 3,
      visible: Boolean(regulation.location_regulation_notes),
      title: 'Location regulation notes',
      body: <TextFromHTML html={regulation.location_regulation_notes} />,
    },
    additionalInformation: {
      order: 4,
      visible: Boolean(regulation.notes),
      title: 'Additional information',
      body: <div>{convertHtmlToReactElement(regulation.notes, ['a'])}</div>,
    },
    edibility: {
      order: 5,
      visible: Boolean(regulation.edibility),
      title: 'Edibility',
      body: <TextFromHTML html={regulation.edibility} />,
    },
    synonyms: {
      order: 6,
      visible: regulation.synonyms.length > 0,
      title: 'Synonyms',
      body: <Text className="mb-0">{regulation.synonyms.join(', ')}</Text>,
    },
    locationSpecificInformation: {
      order: 7,
      visible: locationSpecificInformation.length > 0,
      title: 'Location specific information',
      body: (
        <>
          {locationSpecificInformation.map(info => (
            <TextFromHTML key={info} html={info} />
          ))}
        </>
      ),
    },
  };

  return Object.values(ADDITIONAL_INFORMATION).sort(o => o.order);
};

export { getAdditionalInformation };
